"use client";

import { useState, useEffect } from "react";

export interface BuilderOptionData {
  id: string;
  step: string;
  name: string;
  value: string;
  price: number;
  imageUrl: string | null;
  sortOrder: number;
  isDefault: boolean;
  isActive: boolean;
}

type GroupedOptions = Record<string, BuilderOptionData[]>;

let cachedOptions: GroupedOptions | null = null;
let fetchPromise: Promise<GroupedOptions> | null = null;

/**
 * Fetches builder options from /api/builder/options (grouped by step).
 * Cached in memory for the session — doesn't re-fetch on every render.
 * Components get { options, loading } where options[step] is the array
 * of choices for that step.
 *
 * Falls back to an empty object on error (components should show their
 * hardcoded defaults in that case for resilience).
 */
export function useBuilderOptions() {
  const [options, setOptions] = useState<GroupedOptions>(cachedOptions || {});
  const [loading, setLoading] = useState(!cachedOptions);

  useEffect(() => {
    if (cachedOptions) {
      setOptions(cachedOptions);
      setLoading(false);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = fetch("/api/builder/options")
        .then((r) => (r.ok ? r.json() : {}))
        .then((data: GroupedOptions) => {
          // Convert price from string/Decimal to number
          const normalized: GroupedOptions = {};
          for (const [step, items] of Object.entries(data)) {
            normalized[step] = items.map((item) => ({
              ...item,
              price: Number(item.price),
            }));
          }
          cachedOptions = normalized;
          return normalized;
        })
        .catch(() => {
          cachedOptions = {};
          return {};
        });
    }

    fetchPromise.then((data) => {
      setOptions(data);
      setLoading(false);
    });
  }, []);

  return { options, loading };
}
